import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import {
    MEDIUM_PLAIN_TEXT_LENGTH,
    SHORT_PLAIN_TEXT_LENGTH
} from '../../../constants/common.constants';
import { IUserDTO, IUserGroupCreateDTO } from '../../../api/user-groups.dao';

class UserGroupsFormDataVM {
    public defaultValues = {
        name: '',
        description: '',
        users: []
    };

    @mobx.observable
    public name: string;

    @mobx.observable
    public description: string;

    @mobx.observable
    public users: IUserDTO[];

    @mobx.observable
    private isFormChecked: boolean;

    @mobx.observable
    public serverErrorMessage: string;

    @mobx.observable
    public validationError: string[];

    public initValues: Partial<IUserGroupCreateDTO> = {};

    constructor(private groupData?: IUserGroupCreateDTO) {
        this.name = this.groupData?.name || this.defaultValues.name;
        this.description = this.groupData?.description || this.defaultValues.description;
        this.users = this.groupData?.users || this.defaultValues.users;

        this.initValues = {...{
            name: this.name,
            description: this.description,
            users: this.users
        }};
    }

    @mobx.action
    public setName = (name: string) => {
        this.serverErrorMessage = '';
        this.name = name;
    };
    @mobx.action
    public setDescription = (description: string) => {
        this.serverErrorMessage = '';
        this.description = description;
    };

    public getNameError(intl: IntlShape): string | null {
        const trimmedName = this.name.trim();

        if (!trimmedName) {
            return intl.formatMessage({
                id: `oe.common.nameIsRequired`,
                defaultMessage: 'Name is required',
                description: 'Error message in user popover'
            });
        } else if (trimmedName?.length > SHORT_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.validation.shortPlainTextTooLong`,
                defaultMessage: 'Too long - max length is 255 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    public getDescriptionError(intl: IntlShape): string | null {
        if (this.description?.length > MEDIUM_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.validation.mediumPlainTextTooLong`,
                defaultMessage: 'Too long - max length is 2048 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    public isFormValid(intl: IntlShape): boolean {
        return !this.getNameError(intl) && !this.getDescriptionError(intl);
    }

    @mobx.action
    public allowToShowValidationErrors(): void {
        this.isFormChecked = true;
    }

    public showValidationErrors(): boolean {
        return this.isFormChecked;
    }

    public toApiEditGroupDTO(): IUserGroupCreateDTO {
        return {
            name: this.name.trim(),
            description: this.description.trim(),
            users: this.users
        };
    }

    @mobx.action
    public setUsers = (users: IUserDTO[]) => {
        mobx.runInAction(() => {
            this.users = users.map(user => {
                return {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.username
                };
            });
        });
    };
    @mobx.action
    public removeUser = (username: string) => {
        this.users = this.users.filter(coordinator => coordinator.username !== username);
    };
}

export { UserGroupsFormDataVM };
